import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { 
  Eye, 
  Trash2, 
  Search, 
  Filter, 
  Users, 
  ChevronDown, 
  ChevronUp, 
  ChevronsUpDown,
  Download,
  UserPlus,
  Phone,
  MapPin,
  Calendar,
  X,
  FileText,
  Image
} from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';

const SelfRegisteredMembers = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);

  // Enhanced filters and pagination
  const [filters, setFilters] = useState({
    search: "",
    gender: "",
    disabilityType: "",
    subcity: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: "fullName", direction: "asc" });
  const itemsPerPage = 10;

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem('currentUser'));
      if (user && user.email) {
        const response = await axios.get(`https://aaapd.org.et/gallery_backend/api/getSelfRegisteredMembers.php?email=${encodeURIComponent(user.email)}`);
        setMembers(Array.isArray(response.data) ? response.data : []);
      } else {
        setMembers([]);
      }
    } catch (error) {
      console.error('Failed to fetch self-registered members:', error);
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const onFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  // Enhanced filtering
  const filteredMembers = useMemo(() => {
    return members.filter((member) => {
      const matchesSearch = filters.search
        ? member.fullName.toLowerCase().includes(filters.search.toLowerCase()) ||
          member.phoneNumber.includes(filters.search) ||
          member.subcity.toLowerCase().includes(filters.search.toLowerCase())
        : true;
      
      const matchesGender = filters.gender ? member.gender === filters.gender : true;

      // Support multiple disability types stored as comma-separated string
      const disabilityTypesForMember = (member.disabilityType || '')
        .split(',')
        .map((d) => d.trim())
        .filter(Boolean);

      const matchesDisability = filters.disabilityType
        ? disabilityTypesForMember.includes(filters.disabilityType)
        : true;

      const matchesSubcity = filters.subcity ? member.subcity === filters.subcity : true;

      return matchesSearch && matchesGender && matchesDisability && matchesSubcity;
    });
  }, [members, filters]);

  // Sorting
  const sortedMembers = useMemo(() => {
    const sorted = [...filteredMembers];
    if (sortConfig.key) {
      sorted.sort((a, b) => {
        let aVal = a[sortConfig.key] ?? "";
        let bVal = b[sortConfig.key] ?? "";
        
        if (sortConfig.key === "age") {
          aVal = Number(aVal) || 0;
          bVal = Number(bVal) || 0;
        } else {
          aVal = aVal.toString().toLowerCase();
          bVal = bVal.toString().toLowerCase();
        }
        
        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sorted;
  }, [filteredMembers, sortConfig]);

  const paginatedMembers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedMembers.slice(start, start + itemsPerPage);
  }, [sortedMembers, currentPage]);

  const totalPages = Math.ceil(sortedMembers.length / itemsPerPage);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  // Get unique values for filters
  const uniqueGenders = [...new Set(members.map(m => m.gender).filter(Boolean))];

  // Split comma-separated disabilityType strings into individual types
  const uniqueDisabilityTypes = [
    ...new Set(
      members
        .map((m) => m.disabilityType || '')
        .flatMap((d) =>
          d
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean)
        )
    ),
  ];

  const uniqueSubcities = [...new Set(members.map(m => m.subcity).filter(Boolean))];

  const exportCSV = () => {
    const headers = ["Full Name", "Age", "Gender", "Phone Number", "Disability Type", "Subcity", "City", "Wereda"];
    const rows = sortedMembers.map((member) => [
      member.fullName,
      member.age,
      member.gender,
      member.phoneNumber,
      member.disabilityType,
      member.subcity,
      member.city,
      member.wereda,
    ]);

    let csvContent = "data:text/csv;charset=utf-8," 
      + [headers, ...rows].map(e => e.join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `self-registered-members-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDetailClick = (member) => {
    setSelectedMember(member);
    setIsDetailOpen(true);
  };

  const handleDeleteClick = (member) => {
    setMemberToDelete(member);
    setIsDeleteConfirmOpen(true);
  };

  const handleImageClick = (imageFile) => {
    setSelectedImage(imageFile);
    setImageModalOpen(true);
  };

  const confirmDelete = async () => {
    if (memberToDelete) {
      try {
        const response = await axios.delete(`https://aaapd.org.et/gallery_backend/api/deleteSelfRegisteredMember.php`, {
          data: { id: memberToDelete.id }
        });

        if (response.data.status === 'success') {
          await fetchMembers(); // Refresh the list
        } else {
          console.error('Failed to delete member:', response.data.message);
        }
      } catch (error) {
        console.error('Failed to delete member:', error);
      }
    }
    setIsDeleteConfirmOpen(false);
    setMemberToDelete(null);
  };

  if (loading) {
    return (
      <div className="bg-gray-100 min-h-screen p-4 md:p-8">
        <div className="text-center py-16">
          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-500 text-lg">Loading self-registered members...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen p-4 md:p-8">
      {/* Header */}
      <header className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <UserPlus className="w-8 h-8 text-teal-600" />
          <h1 className="text-4xl font-bold text-gray-800">Self-Registered Members</h1>
        </div>
        <p className="text-gray-500">Manage members who have registered themselves with your OPD.</p>
      </header>

      {/* Control Panel */}
      <div className="bg-white p-6 rounded-xl shadow-md mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Search Input */}
          <div className="relative lg:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="search"
              placeholder="Search by name, phone"
              value={filters.search}
              onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 transition"
            />
          </div>

          {/* Export Button */}
          <div className="flex items-center justify-end">
            <button
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors shadow-sm"
              onClick={exportCSV}
            >
              <Download className="w-5 h-5" />
              Export CSV
            </button>
          </div>

          {/* Gender Filter */}
          <select
            name="gender"
            value={filters.gender}
            onChange={onFilterChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 transition bg-white"
          >
            <option value="">All Genders</option>
            {uniqueGenders.map((gender) => (
              <option key={gender} value={gender}>
                {gender}
              </option>
            ))}
          </select>

          {/* Disability Type Filter */}
          <select
            name="disabilityType"
            value={filters.disabilityType}
            onChange={onFilterChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 transition bg-white"
          >
            <option value="">All Disability Types</option>
            {uniqueDisabilityTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>

        </div>
      </div>

      

      {/* Main Content */}
      {members.length === 0 ? (
        <div className="text-center py-16">
          <UserPlus className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No self-registered members found for your OPD.</p>
        </div>
      ) : filteredMembers.length === 0 ? (
        <div className="text-center py-16">
          <Search className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No members found matching your search criteria.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800">Member List</h3>
          </div>

          {/* Table for Medium screens and up */}
          <div className="overflow-x-auto hidden md:block">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {[
                    { key: 'fullName', label: 'Full Name' },
                    { key: 'age', label: 'Age' },
                    { key: 'gender', label: 'Gender' },
                    { key: 'phoneNumber', label: 'Phone Number' },
                    { key: 'disabilityType', label: 'Disability Type' },
                    { key: 'subcity', label: 'Subcity' },
                  ].map(col => (
                    <th 
                      key={col.key} 
                      onClick={() => requestSort(col.key)}
                      className="py-3 px-4 text-left font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-2">
                        {col.label}
                        {sortConfig.key === col.key ? (
                          sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                        ) : <ChevronsUpDown className="w-4 h-4 text-gray-400" />}
                      </div>
                    </th>
                  ))}
                  <th className="py-3 px-4 text-left font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paginatedMembers.map(member => (
                  <tr key={member.id} className="hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4 text-gray-700 font-medium">{member.fullName}</td>
                    <td className="py-3 px-4 text-gray-600">{member.age}</td>
                    <td className="py-3 px-4 text-gray-600">{member.gender}</td>
                    <td className="py-3 px-4 text-gray-600">{member.phoneNumber}</td>
                    <td className="py-3 px-4 text-gray-600">{member.disabilityType}</td>
                    <td className="py-3 px-4 text-gray-600">{member.subcity}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => handleDetailClick(member)} 
                          className="p-2 text-teal-600 hover:text-teal-800 hover:bg-teal-50 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => handleDeleteClick(member)} 
                          className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete Member"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Cards for Small screens */}
          <div className="block md:hidden">
            <div className="divide-y divide-gray-200">
              {paginatedMembers.map(member => (
                <div key={member.id} className="p-4">
                  <div className="space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-bold text-gray-800">{member.fullName}</p>
                        <p className="text-sm text-gray-600">{member.age} years old • {member.gender}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => handleDetailClick(member)} 
                          className="p-2 text-teal-600 hover:text-teal-800 hover:bg-teal-50 rounded-lg transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => handleDeleteClick(member)} 
                          className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {member.phoneNumber}
                      </div>
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {member.subcity}
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">Disability: {member.disabilityType}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pagination */}
          <div className="flex flex-col md:flex-row justify-between items-center p-4 border-t border-gray-200 gap-4">
            <span className="text-sm text-gray-600">
              Showing {paginatedMembers.length} of {sortedMembers.length} members
            </span>
            <div className="flex items-center gap-2">
              <button
                className="px-3 py-1 border rounded-lg bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Prev
              </button>
              <span className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="px-3 py-1 border rounded-lg bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Detail Modal */}
      <Dialog.Root open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-[1200] backdrop-blur-sm" />
          <Dialog.Content className="fixed z-[1201] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-2xl p-0 w-[90vw] max-w-3xl max-h-[80vh] overflow-hidden">
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Users className="w-6 h-6" />
                  <Dialog.Title className="text-xl font-bold">Member Details</Dialog.Title>
                </div>
                <Dialog.Close asChild>
                  <button className="p-1 hover:bg-white/20 rounded-lg transition-colors">
                    <X className="w-5 h-5" />
                  </button>
                </Dialog.Close>
              </div>
            </div>
            
            {selectedMember && (
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Personal Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">Personal Information</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Full Name</label>
                        <p className="text-gray-800 font-medium">{selectedMember.fullName}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-gray-500">Age</label>
                          <p className="text-gray-800">{selectedMember.age}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500">Gender</label>
                          <p className="text-gray-800">{selectedMember.gender}</p>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Phone Number</label>
                        <p className="text-gray-800 flex items-center gap-2">
                          <Phone className="w-4 h-4 text-gray-400" />
                          {selectedMember.phoneNumber}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Disability Type(s)</label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {(selectedMember.disabilityType || '')
                            .split(',')
                            .map((d) => d.trim())
                            .filter(Boolean)
                            .map((d, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-50 text-teal-700 border border-teal-100"
                              >
                                {d}
                              </span>
                            ))}
                          {!(selectedMember.disabilityType || '').trim() && (
                            <span className="text-gray-500">Not specified</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Address Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">Address Information</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-500">City</label>
                        <p className="text-gray-800">{selectedMember.city}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Subcity</label>
                        <p className="text-gray-800 flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-gray-400" />
                          {selectedMember.subcity}
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-gray-500">Wereda</label>
                          <p className="text-gray-800">{selectedMember.wereda}</p>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">House Number</label>
                        <p className="text-gray-800">{selectedMember.houseNumber}</p>
                      </div>
                    </div>
                  </div>

                  {/* Disability Description */}
                  {selectedMember.disabilityDescription && (
                    <div className="md:col-span-2 space-y-2">
                      <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">Disability Description</h3>
                      <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">{selectedMember.disabilityDescription}</p>
                    </div>
                  )}

                  {/* Message */}
                  {selectedMember.message && (
                    <div className="md:col-span-2 space-y-2">
                      <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">Message</h3>
                      <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">{selectedMember.message}</p>
                    </div>
                  )}

                  {/* ID Attachment (moved to bottom) */}
                  <div className="md:col-span-2 space-y-2">
                    <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">ID Attachment</h3>
                    <div className="text-gray-800">
                      {selectedMember.idAttachment ? (
                        <div className="mt-2">
                          {selectedMember.idAttachment.toLowerCase().endsWith('.pdf') || selectedMember.idAttachment.toLowerCase().endsWith('.doc') || selectedMember.idAttachment.toLowerCase().endsWith('.docx') ? (
                            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                              <FileText className="w-5 h-5 text-teal-600" />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-800">Document file</p>
                                <p className="text-xs text-gray-500">Click to view</p>
                              </div>
                              <a
                                href={selectedMember.idAttachment}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-3 py-1 text-xs bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors"
                              >
                                View
                              </a>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 text-teal-600">
                                <Image className="w-4 h-4" />
                                <span className="text-sm font-medium">Image preview</span>
                              </div>
                              <img
                                src={selectedMember.idAttachment}
                                alt="ID Document"
                                className="max-w-full h-auto max-h-48 rounded-lg border border-gray-200 shadow-sm cursor-pointer hover:opacity-80"
                                onClick={() => handleImageClick(selectedMember.idAttachment)}
                              />
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-500">No document uploaded</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
           
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Enhanced Delete Confirmation Dialog */}
      <Dialog.Root open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-[1200] backdrop-blur-sm" />
          <Dialog.Content className="fixed z-[1201] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-2xl p-0 w-[90vw] max-w-md overflow-hidden">
            <div className="bg-gradient-to-r from-red-600 to-red-700 text-white p-6">
              <div className="flex items-center gap-3">
                <Trash2 className="w-6 h-6" />
                <Dialog.Title className="text-xl font-bold">Confirm Deletion</Dialog.Title>
              </div>
            </div>
            
            <div className="p-6">
              <Dialog.Description className="text-gray-600 mb-6">
                Are you sure you want to delete <strong className="text-gray-800">{memberToDelete?.fullName}</strong>? This action cannot be undone.
              </Dialog.Description>
              
              <div className="flex justify-end gap-3">
                <Dialog.Close asChild>
                  <button className="px-4 py-2 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300 transition-colors">
                    Cancel
                  </button>
                </Dialog.Close>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Enhanced Image Modal */}
      <Dialog.Root open={imageModalOpen} onOpenChange={setImageModalOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/80 z-[1300] backdrop-blur-sm" />
          <Dialog.Content className="fixed z-[1301] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-2xl p-0 w-[95vw] max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Image className="w-6 h-6" />
                  <Dialog.Title className="text-xl font-bold">ID Document Preview</Dialog.Title>
                </div>
                <Dialog.Close asChild>
                  <button className="p-1 hover:bg-white/20 rounded-lg transition-colors">
                    <X className="w-6 h-6" />
                  </button>
                </Dialog.Close>
              </div>
            </div>
            
            {selectedImage && (
              <div className="p-6 flex items-center justify-center bg-gray-50">
                <img
                  src={selectedImage}
                  alt="ID Document Preview"
                  className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg"
                />
              </div>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
};

export default SelfRegisteredMembers;
